import { FC } from 'react';
import { Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Divider, Grid, OutlinedInput, Stack } from '@mui/material';
import { z } from 'zod';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { FormField } from '../../form/FormField';
import { FormErrors } from '../../form/FormErrors';
import { useModalContext } from '../../modal/ModalContext';
import { graphql } from '../../gql';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { useSnackbar } from '../../store/snackbar';
import { useMe } from '../../auth/useMe';

const setGoogleSheetsConfigurationMutation = graphql(`
	mutation googleSheetsModal_setGoogleSheetsConfiguration($spreadsheetId: String!, $userId: UUID!) {
		setGoogleSheetsConfiguration(input: { spreadsheetId: $spreadsheetId, currentUserId: $userId }) {
			ok
			errorMessage
		}
	}
`);
const triggerMetricsSyncMutation = graphql(`
	mutation triggerMetricsSync {
		triggerMetricsSync {
			ok
			errorMessage
		}
	}
`);

const googleSheetsConfigurationQuery = graphql(`
	query googleSheetsModal_googleSheetsConfiguration {
		googleSheetsConfiguration {
			spreadsheetId
		}
	}
`);

const spreadsheetUrlPattern = /^https:\/\/docs.google.com\/spreadsheets\/d\/([^/]+)/;

const testSpreadsheetUrl = (url: string) => spreadsheetUrlPattern.test(url);

const getSpreadsheetIdFromUrl = (url: string) => spreadsheetUrlPattern.exec(url)?.[1];

const getSpreadsheetUrlFromId = (id: string) => `https://docs.google.com/spreadsheets/d/${id}`;

const createFormSchema = (intl: IntlShape) =>
	z.object({
		spreadsheetUrl: z
			.string()
			.url(intl.formatMessage({ id: 'connections.data-sources.google-sheets-modal.error.invalid-url' }))
			.refine(testSpreadsheetUrl, {
				message: intl.formatMessage({ id: 'connections.data-sources.google-sheets-modal.error.invalid-spreadsheet-url' }),
			}),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const googleSheetsModalSchema = z.object({});

type Props = z.infer<typeof googleSheetsModalSchema>;

const GoogleSheetsModal: FC<Props> = (props) => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();
	const { closeModal } = useModalContext();
	const { meData } = useMe();

	const { data, isLoading } = useApiQuery(googleSheetsConfigurationQuery);

	const { mutate: triggerMetricsSync } = useApiMutation(triggerMetricsSyncMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.triggerMetricsSync.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'data-source.setup.sync-spreadsheet.success' }),
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				openSnackbar,
			});
		},
	});

	const form = useTypedForm({
		schema: createFormSchema(intl),
		values: {
			spreadsheetUrl:
				data?.googleSheetsConfiguration?.spreadsheetId != null ? getSpreadsheetUrlFromId(data.googleSheetsConfiguration.spreadsheetId) : '',
		},
	});

	const { mutate: setGoogleSheetsConfiguration, isPending: isSetGoogleSheetsConfigurationPending } = useApiMutation(
		setGoogleSheetsConfigurationMutation,
		{
			onSuccess: (result) => {
				mutationResponseHandler({
					result: result.setGoogleSheetsConfiguration.ok,
					toastMessages: {
						error: intl.formatMessage({ id: 'common.error.default' }),
					},
					openSnackbar,
				});
			},
		},
	);

	const syncSpreadsheetMetrics = () => {
		triggerMetricsSync({});
	};

	const onSubmit = (values: FormValues) => {
		const spreadsheetId = getSpreadsheetIdFromUrl(values.spreadsheetUrl);

		if (spreadsheetId != null) {
			setGoogleSheetsConfiguration(
				{ spreadsheetId, userId: meData?.me.id ?? '' },
				{
					onSuccess: () => {
						closeModal();
					},
				},
			);
		}
	};

	return (
		<form noValidate onSubmit={form.handleSubmit(onSubmit)}>
			<DialogTitle>
				<FormattedMessage id={'form.data-source.setup.title'} />
			</DialogTitle>
			<Divider />
			<DialogContent>
				{isLoading ? (
					<Stack p={3} alignItems={'center'}>
						<CircularProgress />
					</Stack>
				) : (
					<Grid container spacing={3}>
						<FormField
							form={form}
							name={'spreadsheetUrl'}
							label={intl.formatMessage({ id: 'form.data-source.setup.spreadsheet-url' })}
							render={(props) => <OutlinedInput {...props} />}
						/>

						<FormErrors form={form} />
					</Grid>
				)}
			</DialogContent>
			<DialogActions sx={{ p: 2.5 }}>
				<Stack direction={'row'} gap={2} flexWrap={'wrap'} alignItems={'center'} width={'100%'} justifyContent={'space-between'}>
					{/* TODO: Fot this moment we have static configId - a solution will need to be devised  */}
					<Button color={'secondary'} variant={'contained'} onClick={() => syncSpreadsheetMetrics()}>
						<FormattedMessage id={'form.data-source.setup.button.sync-spreadsheet'} />
					</Button>
					<Stack direction={'row'} gap={2}>
						<Button color={'secondary'} onClick={closeModal}>
							<FormattedMessage id={'common.cancel'} />
						</Button>
						<Button type={'submit'} variant={'contained'} disabled={isSetGoogleSheetsConfigurationPending}>
							<FormattedMessage id={'common.create'} />
						</Button>
					</Stack>
				</Stack>
			</DialogActions>
		</form>
	);
};

export { GoogleSheetsModal, googleSheetsModalSchema };
